import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '@services/api'

const BrandingContext = createContext(null)

// Bundled fallback used before the API responds / when it is unreachable.
// DuoNep is the permanent product brand; the organization is the configurable co-brand.
const FALLBACK_BRANDING = {
  orgName: '',
  orgShortName: '',
  orgLogoUrl: null,
  systemSubtitle: 'Computerized Maintenance Management System',
  primaryColor: '#1B5E7A',
  contactEmail: '',
  contactPhone: '',
}

const mapBranding = (data) => ({
  orgName: data.organization_name || FALLBACK_BRANDING.orgName,
  orgShortName: data.organization_short_name || FALLBACK_BRANDING.orgShortName,
  orgLogoUrl: data.organization_logo || null,
  systemSubtitle: data.system_subtitle || FALLBACK_BRANDING.systemSubtitle,
  primaryColor: data.primary_color || FALLBACK_BRANDING.primaryColor,
  contactEmail: data.contact_email || '',
  contactPhone: data.contact_phone || '',
})

export const useBranding = () => {
  const context = useContext(BrandingContext)
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(FALLBACK_BRANDING)
  const [loading, setLoading] = useState(true)

  const fetchBranding = useCallback(async () => {
    try {
      const response = await api.get('/common/branding/')
      setBranding(mapBranding(response.data))
    } catch (error) {
      console.error('Failed to fetch branding:', error)
      // Keep fallback branding on failure.
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBranding()
  }, [fetchBranding])

  const value = {
    ...branding,
    loading,
    refreshBranding: fetchBranding,
  }

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

export default BrandingContext
